import { setCurrentAccountId } from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

// List of all account-dependent React Query cache keys
// IMPORTANT: When adding new account-dependent endpoints, add their query keys here
const ACCOUNT_DEPENDENT_QUERIES = [
  'fetch-account-details',
  'fetch-account-identities',
  'fetch-sub-accounts',
  'fetch-contact-lists',
  'fetch-jobs',
  'fetch-canvassers',
  'fetch-agents',
  'fetch-media',
  'fetch-opt-outs',
  'fetch-link-tracking-domains',
  // TODO: Add other account-dependent queries as they're identified
  // TODO: Implement virtual scrolling for large account lists
  // TODO: Add account data caching strategy
];

// localStorage key for storing the user's account selection
const SELECTED_ACCOUNT_STORAGE_KEY = 'selectedAccountId';

// Section list roots whose detail/edit pages are scoped to a single account's
// entity (e.g. /jobs/[id], /accounts/[id]/edit). After switching accounts that
// entity no longer belongs to the current account, so we route back to the
// section's list page instead of leaving the user on a stale detail URL.
const ACCOUNT_SCOPED_DETAIL_ROOTS = ['/jobs', '/accounts'];

/**
 * Hook for managing account switching functionality
 * Handles URL updates, localStorage persistence, and cache invalidation
 */
const useAccountSwitching = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  /**
   * Switch to a different account
   * This will:
   * 1. Update currentAccountId in Redux
   * 2. Update URL query parameter
   * 3. Store selection in localStorage
   * 4. Invalidate all account-dependent caches
   */
  const switchToAccount = useCallback(
    async (accountId: string) => {
      try {
        // 1. Update current account in Redux
        dispatch(setCurrentAccountId(accountId));

        // 2. Update URL query parameter, redirecting away from any
        // account-scoped detail page to its list so we don't strand the user
        // on the previous account's entity (e.g. a job or account config).
        const params = new URLSearchParams(searchParams.toString());
        params.set('account', accountId);
        const detailRoot = ACCOUNT_SCOPED_DETAIL_ROOTS.find((root) =>
          pathname.startsWith(`${root}/`)
        );
        const targetPath = detailRoot ?? pathname;
        router.push(`${targetPath}?${params.toString()}`, { scroll: false });

        // 3. Store selection in localStorage
        localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, accountId);

        // 4. Invalidate account-specific caches only
        // Don't invalidate 'fetch-sub-accounts' as those are managed separately
        await Promise.all(
          ACCOUNT_DEPENDENT_QUERIES.filter((key) => key !== 'fetch-sub-accounts').map(
            (queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] })
          )
        );

        // Specifically invalidate sub-accounts for the new current account only
        queryClient.invalidateQueries({
          queryKey: ['fetch-sub-accounts', { rootAccountId: accountId }],
        });

        console.log(`Successfully switched to account: ${accountId}`);
      } catch (error) {
        console.error('Error switching accounts:', error);
        // TODO: Show error toast message
      }
    },
    [dispatch, queryClient, router, searchParams, pathname]
  );

  /**
   * Get the account ID that should be selected based on priority:
   * 1. URL query parameter (highest priority)
   * 2. Previous selection from localStorage
   * 3. Default fallback (provided by caller)
   */
  const getSelectedAccountId = useCallback(
    (rootAccounts: string[] | null): string | null => {
      if (!rootAccounts || rootAccounts.length === 0) return null;

      // 1. Check URL query parameter first
      const urlAccountId = searchParams.get('account');
      if (urlAccountId) {
        return urlAccountId;
      }

      // 2. Check localStorage for previous selection
      try {
        const storedAccountId = localStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY);
        if (storedAccountId) {
          return storedAccountId;
        }
      } catch (error) {
        console.warn('Error reading from localStorage:', error);
      }

      // 3. Default to first root account
      return rootAccounts[0];
    },
    [searchParams]
  );

  return {
    switchToAccount,
    getSelectedAccountId,
  };
};

export default useAccountSwitching;
